import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Router } from '@angular/router';
import { concatLatestFrom } from '@ngrx/operators';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { WorkspaceDetailEffects } from '../../../stores/workspace-detail.effects';

/**
 * Facade state is empty; all state is delegated to WorkspaceDetailState (via WorkspaceDetailEffects).
 */
type WorkspaceDetailFacadeState = Record<string, never>;

@Injectable()
export class WorkspaceDetailFacade extends ComponentStore<WorkspaceDetailFacadeState> {
  constructor(private readonly detailEffects: WorkspaceDetailEffects, private readonly router: Router) {
    super({});
  }

  // Delegated selectors
  public get workspace$() {
    return this.detailEffects.data$;
  }

  public get numCases$(): Observable<number> {
    return this.detailEffects.numCases$;
  }

  public get numMembers$(): Observable<number> {
    return this.detailEffects.numMembers$;
  }

  public get caseTypeName$(): Observable<string> {
    return this.detailEffects.caseTypeName$;
  }

  public get loading$() {
    return this.detailEffects.loading$;
  }

  public get error$() {
    return this.detailEffects.error$;
  }

  public get deletingWorkspace$() {
    return this.detailEffects.deleting$;
  }

  public get workspaceDeletionError$() {
    return this.detailEffects.deletionError$;
  }

  public get canBeDeleted$(): Observable<boolean> {
    return this.numCases$.pipe(map((count) => count === 0));
  }

  // Effects
  public readonly initialize = this.effect((workspaceId$: Observable<string>) =>
    workspaceId$.pipe(
      tap((workspaceId: string) => {
        this.detailEffects.loadDetail({ workspaceId });
      })
    )
  );

  public readonly deleteWorkspace = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => this.detailEffects.id$),
      switchMap(([, workspaceId]) => {
        if (!workspaceId) return [];
        this.detailEffects.deleteWorkspace(workspaceId);
        return this.detailEffects.deleteResult$.pipe(
          takeUntil(this.destroy$),
          take(1),
          filter((result) => result.success),
          tap(() => this.router.navigate(['/workspaces']))
        );
      })
    )
  );
}
